import { z } from 'zod';

/**
 * 会員IDバリデーションスキーマを動的に生成
 * @param {string} pattern - APIから取得した正規表現パターン文字列（必須）
 * @param {number} minDigits - 有効桁数（最小）
 * @param {number} maxDigits - 有効桁数（最大）
 * @param {number} [validationType] - バリデーションチェックNo（1-15）（後方互換性のためオプショナル）
 * @returns {z.ZodObject} Zodスキーマ
 */
export const createMemberIdSchema = (pattern, minDigits, maxDigits, validationType) => {
  // バリデーションタイプに応じた正規表現パターンを取得（後方互換性のため）
  const getPatternByValidationType = (type) => {
    const patterns = {
      1: /^[0-9]+$/, // 数字のみ
      2: /^[a-z]+$/, // 英小文字のみ
      3: /^[A-Z]+$/, // 英大文字のみ
      4: /^[\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]+$/, // 記号のみ
      5: /^[0-9a-z]+$/, // 数字、英小文字
      6: /^[0-9A-Z]+$/, // 数字、英大文字
      7: /^[\x30-\x39\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]+$/, // 数字、記号
      8: /^[a-zA-Z]+$/, // 英小文字、英大文字
      9: /^[\x61-\x7a\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]+$/, // 英小文字、記号
      10: /^[\x41-\x5a\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]+$/, // 英大文字、記号
      11: /^[0-9a-zA-Z]+$/, // 数字、英小文字、英大文字
      12: /^[\x30-\x39\x61-\x7a\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]+$/, // 数字、英小文字、記号
      13: /^[\x30-\x39\x41-\x5a\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]+$/, // 数字、英大文字、記号
      14: /^[\x61-\x7a\x41-\x5a\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]+$/, // 英小文字、英大文字、記号
      15: /^[\x30-\x39\x61-\x7a\x41-\x5a\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]+$/, // 数字、英小文字、英大文字、記号
    };
    return patterns[type] || /^.+$/; // デフォルトは任意の文字
  };

  // パターンの処理: patternが提供されている場合はそれを使用、なければvalidationTypeから生成
  let regexPattern;
  if (pattern && typeof pattern === 'string') {
    try {
      // APIから取得したパターン文字列を正規表現オブジェクトに変換
      regexPattern = new RegExp(pattern);
    } catch (e) {
      // パターンが無効な場合はvalidationTypeから生成、またはデフォルト
      regexPattern = validationType ? getPatternByValidationType(validationType) : /^.+$/;
    }
  } else if (validationType) {
    // 後方互換性: validationTypeから生成
    regexPattern = getPatternByValidationType(validationType);
  } else {
    // デフォルトパターン
    regexPattern = /^.+$/;
  }

  const minLength = minDigits || 1;
  const maxLength = maxDigits || 255;

  return z
    .object({
      memberId: z
        .string()
        .min(1, '会員IDは必須です')
        .min(minLength, `会員IDは${minLength}文字以上で入力してください`)
        .max(maxLength, `会員IDは${maxLength}文字以内で入力してください`)
        .regex(regexPattern, '会員IDの形式が正しくありません'),
      memberIdConfirmation: z.string().min(1, '確認用会員IDは必須です'),
    })
    .refine((data) => data.memberId === data.memberIdConfirmation, {
      message: '入力内容が確認用の値と一致しません。',
      path: ['memberIdConfirmation'],
    });
};

